// Imports for external libraries go here.
import React, { FC, useState } from 'react';
import clsx from 'clsx';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
import { Icon, Types, Text, InputTextField } from '@marriott/mi-ui-library';
// The closer the import is to the file the lower it should be in this list.
import { PasswordInputProps } from './PasswordInput.types';
import { StyledPasswordInput } from './PasswordInput.styles';

// Use named rather than default exports.
export const PasswordInput: FC<PasswordInputProps> = ({
  hasError,
  value,
  disable,
  label,
  name,
  id,
  passwordType,
  error,
  isValid,
  iconClass,
  customClass,
  labelClass,
  showValidations,
  passwordConditions,
  inputCustomClass,
  showCheckIcon,
  onIconClick,
  handleChange,
  ariaLabel,
  descriptionClass,
  errorLabelClass,
  innerLabel,
  checkHeaderLabel,
  addBottomMargin,
}) => {
  const [isActive, setIsActive] = useState(false);
  // Checks must be visible only when :
  // 1. input field is active even if user have not entered any value
  // 2. however if user enters any value checks must be visible and must not be hidden
  const showValidationChecks =
    showValidations && (!innerLabel || (innerLabel && ((value?.length && value?.length > 0) || isActive)));
  return (
    <StyledPasswordInput
      className="m-password-field-group-v3"
      data-component-name="m-account-PasswordInput"
      data-testid="account-PasswordInput"
    >
      <div
        className={clsx(
          innerLabel ? 'm-input-password-field-v3' : 'm-input-field',
          disable ? 'is-disabled' : hasError ? 'is-error' : value ? 'is-active' : '',
          customClass
        )}
      >
        <div className="input-wrapper">
          <InputTextField
            infoLabel={id}
            label={label}
            inputValue={value}
            inputMaxLength={20}
            className={inputCustomClass ?? ''}
            disabled={disable}
            getLabelProps={() => ({
              htmlFor: id,
              className: labelClass,
            })}
            getInputProps={() => ({
              autoComplete: 'off',
              id,
              name,
              type: passwordType,
              'aria-label': ariaLabel,
              'data-testId': id,
            })}
            getInputValue={(value: string) => handleChange?.(name || '', value)}
            customDownshiftLabel
            messageClass={clsx('message', errorLabelClass)}
            testId="password-input"
            showIcon={false}
            onFocus={() => setIsActive(true)}
            onBlur={() => setIsActive(false)}
          />

          <button
            type="button"
            className="password-toggle"
            aria-label={ariaLabel}
            disabled={disable}
            onClick={!disable ? onIconClick : undefined}
          >
            <span className={clsx(`icon ${disable && 'fade-icon'}`, iconClass)} />
          </button>
        </div>
        {!!innerLabel && hasError && (
          <>
            <span className="message">{error}</span>
          </>
        )}
        {!innerLabel && showCheckIcon && (
          <Icon
            iconClass={clsx(
              'icon password-valid-check',
              innerLabel ? 'password-valid-check__inline' : 'password-valid-check__standard',
              isValid ? 'icon-check green-check' : value?.length ? 'icon-clear red-clear' : ''
            )}
          />
        )}
        {showValidationChecks && passwordConditions?.length && passwordConditions?.length > 0 && (
          <div className={clsx('validation-rules', addBottomMargin && 'mb-5', disable && 'is-disabled')}>
            {innerLabel && (
              <Text
                copyText={checkHeaderLabel}
                fontSize={Types.size.small}
                element={Types.tags.span}
                customClass="rules-heading"
              />
            )}

            <div className="rules-list">
              {passwordConditions?.map(password => (
                <div
                  key={password.condition}
                  className={clsx('rule', {
                    'is-valid': password.isValid,
                    'is-pending': !password.isValid && isActive,
                    'is-invalid': !password.isValid && !isActive && value?.length,
                  })}
                >
                  <Icon
                    iconClass={clsx(
                      value?.length
                        ? {
                            'icon-check green-check': password.isValid,
                            'icon-dot': !password.isValid && isActive,
                            'icon-cancel red-clear': !password.isValid && !isActive,
                          }
                        : 'icon icon-dot'
                    )}
                  />

                  <Text
                    copyText={password.condition}
                    fontSize={innerLabel ? Types.size.small : Types.size.extraSmall}
                    element={Types.tags.span}
                    customClass={clsx(descriptionClass ?? '', {
                      'is-valid': password.isValid,
                      'is-invalid': !password.isValid && !isActive && value?.length,
                    })}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </StyledPasswordInput>
  );
};
